import dayjs, { Dayjs } from 'dayjs';
import { Workbook } from 'exceljs';
import {
  BbLbBoardCatalogEntry,
  BbLbMetric,
  BbLbPhaseBoard,
  BbLbRankingExport,
  BbLbRewardKind,
  BbLbSource,
} from '../../../services/blindBoxService';

export const SOURCE_I18N: Record<BbLbSource, string> = {
  FUTURES: 'module.blindBox.lbSource_FUTURES',
  PREDICTION_SPOT: 'module.blindBox.lbSource_PREDICTION_SPOT',
  PREDICTION_LEVERAGE: 'module.blindBox.lbSource_PREDICTION_LEVERAGE',
  PREDICTION_SPOT_LEVERAGE: 'module.blindBox.lbSource_PREDICTION_SPOT_LEVERAGE',
  TAPGRID: 'module.blindBox.lbSource_TAPGRID',
};

export const METRIC_I18N: Record<BbLbMetric, string> = {
  VOLUME: 'module.blindBox.lbMetric_VOLUME',
  PNL: 'module.blindBox.lbMetric_PNL',
};

export const REWARD_KIND_I18N: Record<BbLbRewardKind, string> = {
  BOX: 'module.blindBox.lbRewardKind_BOX',
  USDC: 'module.blindBox.lbRewardKind_USDC',
};

/** Phase / entry status → chip meta. */
export const STATUS_META: Record<string, { key: string; color: 'info' | 'success' | 'default' }> = {
  upcoming: { key: 'module.blindBox.lbStatusUpcoming', color: 'info' },
  active: { key: 'module.blindBox.lbStatusActive', color: 'success' },
  completed: { key: 'module.blindBox.lbStatusCompleted', color: 'default' },
};

export const PAYOUT_STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'info',
  completed: 'success',
  failed: 'error',
  projected: 'default', // live-preview entries (phase not yet frozen)
};

// ── formatters ────────────────────────────────────────────────────────────────

export const toDayjs = (v?: string | number | null): Dayjs | null => {
  if (v === null || v === undefined || v === '') return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

export const fmtTime = (v?: string | number | null): string => {
  const d = toDayjs(v);
  return d ? d.format('YYYY-MM-DD HH:mm') : '—';
};

export const toNum = (v?: string | number | null): number => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

/** Thousands-separated number, up to 2 decimals. */
export const fmtNum = (v?: string | number | null): string =>
  toNum(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** Reward amount with its unit (boxes vs USDC). */
export const fmtReward = (
  amount: string | number | null | undefined,
  kind: BbLbRewardKind,
  t: (k: string) => string
): string => `${fmtNum(amount)} ${kind === 'BOX' ? t('module.blindBox.lbUnitBox') : 'USDC'}`;

export const shortId = (s?: string | null): string => {
  const v = String(s ?? '');
  return v.length <= 14 ? v : `${v.slice(0, 6)}…${v.slice(-4)}`;
};

/** Keep digits only. */
export const digitsOnly = (s: string) => s.replace(/[^0-9]/g, '');

/** Keep digits and a single decimal point. */
export const decimalOnly = (s: string) => {
  const cleaned = s.replace(/[^0-9.]/g, '');
  const [intPart, ...rest] = cleaned.split('.');
  return rest.length ? `${intPart}.${rest.join('')}` : cleaned;
};

/** "source · metric" for a (source, metric) pair. */
export const sourceMetricLabel = (
  source: BbLbSource,
  metric: BbLbMetric,
  t: (k: string) => string
): string => `${t(SOURCE_I18N[source])} · ${t(METRIC_I18N[metric])}`;

/** Board display name — the board's own label, falling back to "source · metric". */
export const boardLabel = (
  board: Pick<BbLbPhaseBoard, 'label' | 'source' | 'metric'>,
  t: (k: string) => string
): string => board.label?.trim() || sourceMetricLabel(board.source, board.metric, t);

/** Catalog-entry display name — its default label, falling back to "source · metric". */
export const catalogLabel = (
  entry: BbLbBoardCatalogEntry,
  t: (k: string) => string
): string => entry.defaultLabel?.trim() || sourceMetricLabel(entry.source, entry.metric, t);

// ── ranking export (xlsx) ─────────────────────────────────────────────────────

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Excel sheet names cannot contain []:*?/\ and are capped at 31 chars; keep them unique. */
const sheetName = (raw: string, index: number, used: Set<string>): string => {
  const base = (raw || `board-${index + 1}`).replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 28) || `board-${index + 1}`;
  let name = base;
  let n = 2;
  while (used.has(name.toLowerCase())) {
    name = `${base.slice(0, 28)} ${n}`;
    n += 1;
  }
  used.add(name.toLowerCase());
  return name;
};

/**
 * Download a phase's grouped ranking export as an .xlsx file — one worksheet per board,
 * each carrying the board's ranked rows.
 */
export async function downloadRankingXlsx(
  filename: string,
  data: BbLbRankingExport
): Promise<void> {
  const wb = new Workbook();
  const used = new Set<string>();
  data.boards.forEach((board, i) => {
    const ws = wb.addWorksheet(sheetName(board.label || `${board.source}-${board.metric}`, i, used));
    // The ranked-stat column is the board's metric — volume or profit.
    const valueHeader = board.metric === 'PNL' ? 'Profit' : 'Volume';
    ws.addRow([
      'Rank',
      'User Id',
      'Wallet Address',
      valueHeader,
      'USDC Reward',
      'Boxes Reward',
      'Achieved At',
      'Banned',
    ]);
    ws.getRow(1).font = { bold: true };
    board.rows.forEach((r) =>
      ws.addRow([
        r.rank,
        r.userId ?? '',
        r.walletAddress ?? '',
        String(r.value ?? ''),
        String(r.usdcRewardAmount ?? ''),
        String(r.boxRewardAmount ?? ''),
        r.achievedAt ? dayjs(r.achievedAt).format('YYYY-MM-DD HH:mm:ss') : '',
        r.isBanned ? 'Yes' : 'No',
      ])
    );
    ws.columns.forEach((c) => {
      c.width = 22;
    });
  });
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
